// src/components/usuario/ForosUsuario.jsx
import React, { useEffect, useState, useRef, useMemo } from "react";
import axios from "axios";
import { API_BASE } from "../../../../api";
import { ENDPOINTS } from "../../Utils/Endpoints";

import { getSessionUserId } from "../../../../utils/SessionUser";
import { toISODateTime, parseDateTimeFlexible } from "../../../../utils/DateHelpers";

// Helper para construir la URL completa de la imagen
const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http") || path.startsWith("data:")) return path;
    const baseUrl = API_BASE.replace(/\/api\/?$/, "");
    return `${baseUrl}${path.startsWith('/') ? '' : '/'}${path}`;
};

// Placeholder image
const PLACEHOLDER_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='250' viewBox='0 0 400 250'%3E%3Crect fill='%23E5E7EB' width='400' height='250'/%3E%3Cpath fill='%239CA3AF' d='M163 142.5l24-24 24 24 48-48 48 48V187H163v-44.5zm48-39.5c-8.3 0-15-6.7-15-15s6.7-15 15-15 15 6.7 15 15-6.7 15-15 15z'/%3E%3C/svg%3E";

const formatFechaCorta = (fechaStr) => {
    if (!fechaStr) return "";
    const d = new Date(fechaStr);
    if (!d || Number.isNaN(d.getTime())) return fechaStr;
    return d.toLocaleString('es-MX', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    }).toLowerCase();
};

const CommentList = ({ comentarios, formatFecha, foroSeleccionado, fotosMap = {} }) => {
    const messagesEndRef = useRef(null);
    const containerRef = useRef(null);
    const [isAtBottom, setIsAtBottom] = useState(true);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    // Scroll inicial y cuando cambian comentarios (si estaba abajo)
    useEffect(() => {
        if (isAtBottom) {
            scrollToBottom();
        }
    }, [comentarios, isAtBottom]);

    const handleScroll = () => {
        if (containerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
            // Tolerancia de 50px para considerar que está "abajo"
            const atBottom = scrollHeight - scrollTop - clientHeight < 50;
            setIsAtBottom(atBottom);
        }
    };

    return (
        <div
            ref={containerRef}
            onScroll={handleScroll}
            style={{
                flex: 1,
                overflowY: 'auto',
                padding: '1rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '1.25rem'
            }}
        >
            {comentarios.length > 0 ? (
                comentarios.map((comentario, index) => {
                    const initials = comentario.autor
                        ? comentario.autor.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                        : "U";
                    const bgColors = ['#FCA5A5', '#FCD34D', '#34D399', '#60A5FA', '#818CF8', '#A78BFA', '#F472B6'];
                    const avatarColor = bgColors[index % bgColors.length];

                    const isOwner = Number(comentario.idUsuario) === Number(foroSeleccionado?.creadoPor);
                    const userFoto = fotosMap[String(comentario.idUsuario)];

                    return (
                        <div key={comentario.id || index} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                            <div style={{
                                width: '32px',
                                height: '32px',
                                minWidth: '32px',
                                borderRadius: '50%',
                                backgroundColor: (userFoto && userFoto !== "") ? 'transparent' : (isOwner ? '#0f172a' : avatarColor),
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.8rem',
                                fontWeight: 'bold',
                                marginTop: '2px',
                                border: isOwner && (!userFoto || userFoto === "") ? '2px solid #e2e8f0' : 'none',
                                overflow: 'hidden'
                            }}>
                                {(userFoto && userFoto !== "") ? (
                                    <img
                                        src={getImageUrl(userFoto)}
                                        alt={comentario.autor}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling.style.display = 'flex'; }}
                                    />
                                ) : null}
                                <div style={{ display: (userFoto && userFoto !== "") ? 'none' : 'flex', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                                    {isOwner ? '👑' : initials}
                                </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.1rem' }}>
                                    <span style={{ fontSize: '0.85rem', fontWeight: 'bold', color: isOwner ? '#0f172a' : '#334155' }}>
                                        {comentario.autor || 'Anónimo'}
                                        {isOwner && <span style={{
                                            marginLeft: '6px',
                                            fontSize: '0.65rem',
                                            backgroundColor: '#f1f5f9',
                                            color: '#475569',
                                            padding: '1px 6px',
                                            borderRadius: '8px',
                                            border: '1px solid #e2e8f0',
                                            verticalAlign: 'middle'
                                        }}>AUTOR</span>}
                                    </span>
                                    <span style={{ fontSize: '0.7rem', color: '#94a3b8', marginLeft: 'auto', textAlign: 'right' }}>
                                        {formatFecha(comentario.fecha)}
                                    </span>
                                </div>
                                <p style={{
                                    margin: 0,
                                    color: '#475569',
                                    fontSize: '0.9rem',
                                    lineHeight: '1.4',
                                    wordBreak: 'break-word',
                                    backgroundColor: isOwner ? '#f8fafc' : 'transparent',
                                    padding: isOwner ? '6px 10px' : '0',
                                    borderRadius: isOwner ? '8px' : '0',
                                    border: isOwner ? '1px solid #f1f5f9' : 'none'
                                }}>
                                    {comentario.mensaje}
                                </p>
                            </div>
                        </div>
                    );
                })
            ) : (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    color: '#94a3b8',
                    gap: '0.5rem',
                    textAlign: 'center',
                    padding: '2rem 1rem'
                }}>
                    <span style={{ fontSize: '2rem' }}>💬</span>
                    <p style={{ margin: 0 }}>Aún no hay respuestas.</p>
                    <p style={{ fontSize: '0.8rem', margin: 0 }}>Sé el primero en contestar.</p>
                </div>
            )}
            <div ref={messagesEndRef} />
        </div>
    );
};

const ForosUsuario = ({ onVolver, initialItem }) => {
    const [foros, setForos] = useState([]);
    const [respuestas, setRespuestas] = useState([]);
    const [loading, setLoading] = useState(true);

    const [showModal, setShowModal] = useState(!!initialItem);
    const [foroSeleccionado, setForoSeleccionado] = useState(initialItem || null);
    const [nuevoComentario, setNuevoComentario] = useState("");
    const [nombreUsuario, setNombreUsuario] = useState("");
    const [fotoUsuario, setFotoUsuario] = useState(null);
    const [categorias, setCategorias] = useState([]);
    const [usuariosMap, setUsuariosMap] = useState({});
    const [fotosMap, setFotosMap] = useState({});

    const [modalTab, setModalTab] = useState('info'); // 'info' o 'comments'

    // Estado para filtros
    const [filtros, setFiltros] = useState({
        texto: '',
        categoria: 'Todas'
    });

    useEffect(() => {
        cargarDatos();
        cargarNombreUsuario();
    }, []);

    const cargarNombreUsuario = async () => {
        try {
            const userId = getSessionUserId();
            console.log("User ID from session:", userId);

            if (!userId) {
                console.warn("No user ID found in session, using fallback");
                setNombreUsuario("Usuario");
                return;
            }

            const [resUsuarios, resPersonas] = await Promise.all([
                axios.get(ENDPOINTS.usuarios),
                axios.get(ENDPOINTS.personas)
            ]);

            console.log("Usuarios:", resUsuarios.data.length);
            console.log("Personas:", resPersonas.data.length);

            // Buscar el usuario actual
            const usuarioActual = resUsuarios.data.find(u => String(u.idUsuario || u.IdUsuario) === String(userId));
            console.log("Usuario actual:", usuarioActual);

            if (!usuarioActual) {
                console.warn("Usuario no encontrado, usando fallback");
                setNombreUsuario("Usuario");
                return;
            }

            // Buscar la persona asociada
            const personaId = usuarioActual.idPersona || usuarioActual.IdPersona;
            const personaActual = resPersonas.data.find(p => (p.idPersona || p.IdPersona) === personaId);
            console.log("Persona actual:", personaActual);

            if (!personaActual) {
                console.warn("Persona no encontrada, usando fallback");
                setNombreUsuario("Usuario");
                return;
            }

            // Construir nombre completo
            const nombre = personaActual.nombre || personaActual.Nombre || '';
            const apP = personaActual.apellidoPaterno || personaActual.ApellidoPaterno || '';
            const apM = personaActual.apellidoMaterno || personaActual.ApellidoMaterno || '';
            const nombreCompleto = `${nombre} ${apP} ${apM}`.trim();
            console.log("Nombre completo:", nombreCompleto);
            setNombreUsuario(nombreCompleto || "Usuario");

            // Crear el usuariosMap para reconocimiento de autores
            const uMap = {};
            const fMap = {};
            const pMap = {};
            const pfMap = {};
            resPersonas.data.forEach(p => {
                const idP = String(p.idPersona || p.IdPersona || 0);
                pMap[idP] = `${p.nombre || p.Nombre} ${p.apellidoPaterno || p.ApellidoPaterno} ${p.apellidoMaterno || p.ApellidoMaterno || ''}`.trim();
                pfMap[idP] = p.fotoPerfil || p.FotoPerfil || null;
            });
            resUsuarios.data.forEach(u => {
                const pId = String(u.idPersona || u.IdPersona || 0);
                const uId = String(u.idUsuario || u.IdUsuario || 0);
                if (pId !== "0" && pMap[pId]) {
                    uMap[uId] = pMap[pId];
                    if (pfMap[pId]) fMap[uId] = pfMap[pId];
                }
            });
            setUsuariosMap(uMap);
            setFotosMap(fMap);

            const sUserId = String(userId);
            if (fMap[sUserId]) {
                setFotoUsuario(fMap[sUserId]);
            }
        } catch (error) {
            console.error("Error al cargar nombre de usuario:", error);
            setNombreUsuario("Usuario"); // Fallback en caso de error
        }
    };

    const cargarDatos = async () => {
        try {
            setLoading(true);
            const idUsuario = getSessionUserId();
            if (!idUsuario) return;

            const [resForos, resRespuestas, resCategorias, resEstados] = await Promise.all([
                axios.get(ENDPOINTS.forosDashboardByUsuarioId(idUsuario)).catch(e => ({ data: [] })),
                axios.get(ENDPOINTS.forosRespuestas).catch(e => ({ data: [] })),
                axios.get(ENDPOINTS.categorias).catch(e => ({ data: [] })),
                axios.get(ENDPOINTS.estadosIntranet).catch(e => ({ data: [] }))
            ]);

            const catsMap = {};
            resCategorias.data.forEach(c => catsMap[c.idCategoria || c.IdCategoria] = c.nombre || c.Nombre);

            const estadosMap = {};
            resEstados.data.forEach(e => {
                const id = e.idEstado || e.IdEstado;
                const nombre = (e.nombre || e.Nombre || "").toLowerCase();
                estadosMap[id] = nombre;
            });

            setCategorias(resCategorias.data || []);

            const forosData = resForos.data
                .map(f => {
                    const idEstadoForo = f.idEstado || f.IdEstado;
                    const nombreEstado = estadosMap[idEstadoForo] || "abierto";
                    return {
                        id: f.idForo || f.IdForo,
                        titulo: f.titulo || f.Titulo,
                        descripcion: f.descripcionInicial || f.DescripcionInicial,
                        categoria: f.nombreCategoria || f.NombreCategoria || catsMap[f.idCategoria || f.IdCategoria] || "General",
                        fecha: f.fechaCreacion || f.FechaCreacion,
                        imagen: f.imagenUrl || f.ImagenUrl,
                        activo: f.activo !== undefined ? f.activo : f.Activo,
                        estado: nombreEstado,
                        nombreArea: f.nombreArea || f.NombreArea || "Sin área",
                        idEstado: idEstadoForo,
                        creadoPor: f.idUsuarioCreador || f.IdUsuarioCreador,
                        destacado: f.destacado || f.Destacado || false
                    };
                })
                .filter(f => f.estado === "abierto");

            const respuestasData = resRespuestas.data.map(r => {
                const nombreAutor = usuariosMap[r.idUsuario || r.IdUsuario] || r.autor || r.Autor || "Anónimo";
                return {
                    id: r.idRespuesta || r.IdRespuesta,
                    idForo: r.idForo || r.IdForo,
                    idUsuario: r.idUsuario || r.IdUsuario,
                    autor: nombreAutor,
                    mensaje: r.mensaje || r.Mensaje,
                    fecha: r.fechaCreacion || r.FechaCreacion
                };
            });

            const ordenados = forosData.sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());

            setForos(ordenados);
            setRespuestas(respuestasData);

        } catch (e) {
            console.error("Error cargando foros:", e);
        } finally {
            setLoading(false);
        }
    };

    const handleFiltrosChange = (e) => {
        const { name, value } = e.target;
        setFiltros(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const forosFiltrados = useMemo(() => {
        return foros.filter(foro => {
            const matchTexto = !filtros.texto ||
                (foro.titulo || "").toLowerCase().includes(filtros.texto.toLowerCase()) ||
                (foro.descripcion || "").toLowerCase().includes(filtros.texto.toLowerCase());

            const matchCategoria = filtros.categoria === 'Todas' || foro.categoria === filtros.categoria;

            return matchTexto && matchCategoria;
        });
    }, [foros, filtros]);

    const handleVerDetalle = (foro) => {
        setForoSeleccionado(foro);
        setShowModal(true);
        setModalTab('info');
        setNuevoComentario("");
    };

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleEnviarComentario = async (e) => {
        if (e) e.preventDefault();

        if (!nuevoComentario.trim() || !nombreUsuario) {
            // Optional: remove alert for better UX or keep as toast
            return;
        }

        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            const userId = getSessionUserId();

            if (!userId) {
                console.error("Error de sesión: No se pudo identificar al usuario.");
                setIsSubmitting(false);
                return;
            }

            // Calcular fecha para asegurar que vaya al final (resolver timestamps futuros)
            const now = new Date();
            const currentComments = getComentariosForo(foroSeleccionado.id);
            let fechaFinal = now;

            if (currentComments.length > 0) {
                const lastComment = currentComments[currentComments.length - 1];
                const lastDate = new Date(lastComment.fecha);
                if (!isNaN(lastDate.getTime()) && lastDate >= now) {
                    fechaFinal = new Date(lastDate.getTime() + 1000); // Sumar 1 segundo al último
                }
            }

            const payload = {
                IdRespuesta: 0,
                IdForo: foroSeleccionado.id,
                Mensaje: nuevoComentario.trim(),
                IdUsuario: Number(userId),
                Autor: nombreUsuario,
                FechaCreacion: toISODateTime(fechaFinal)
            };

            console.log("Enviando comentario con payload:", payload);

            const response = await axios.post(ENDPOINTS.forosRespuestas, payload);

            if (response.status === 200 || response.status === 201) {
                const nuevaRespuesta = {
                    id: response.data || Math.random(),
                    idForo: foroSeleccionado.id,
                    idUsuario: payload.IdUsuario,
                    autor: payload.Autor,
                    mensaje: payload.Mensaje,
                    fecha: payload.FechaCreacion
                };

                setRespuestas(prev => [...prev, nuevaRespuesta]);
                setNuevoComentario("");
            }
        } catch (error) {
            console.error("Error al enviar comentario:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const getComentariosForo = (idForo) => {
        return respuestas
            .filter(r => r.idForo === idForo)
            .sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
    };

    return (
        <div className="intra-avisos-page">
            {/* HEADER */}
            <header className="intra-news-header">
                <div>
                    {onVolver && (
                        <button
                            type="button"
                            className="intra-button-back"
                            onClick={onVolver}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.5rem 1rem',
                                marginBottom: '1rem',
                                backgroundColor: 'transparent',
                                border: '1px solid #cbd5e1',
                                borderRadius: '8px',
                                color: '#475569',
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f1f5f9';
                                e.currentTarget.style.transform = 'translateX(-4px)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.transform = 'translateX(0)';
                            }}
                        >
                            <span style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>←</span>
                            <span>Volver al dashboard</span>
                        </button>
                    )}
                    <h1 className="intra-news-title">Foros temáticos</h1>
                </div>
            </header>

            {/* FILTROS */}
            <section className="intra-news-toolbar">
                <div className="intra-news-filters" style={{ width: '100%', display: 'flex', gap: '1rem', alignItems: 'flex-end', marginBottom: '1.5rem' }}>
                    <div className="intra-filter-group" style={{ flex: '1', minWidth: '200px' }}>
                        <label className="intra-label" htmlFor="filtroTexto">
                            Buscar
                        </label>
                        <input
                            id="filtroTexto"
                            name="texto"
                            type="text"
                            className="intra-input"
                            value={filtros.texto}
                            onChange={handleFiltrosChange}
                            placeholder="Buscar por título o descripción..."
                        />
                    </div>

                    <div className="intra-filter-group" style={{ width: '250px' }}>
                        <label className="intra-label" htmlFor="filtroCategoria">
                            Categoría
                        </label>
                        <select
                            id="filtroCategoria"
                            name="categoria"
                            className="intra-select"
                            value={filtros.categoria}
                            onChange={handleFiltrosChange}
                        >
                            <option value="Todas">Todas</option>
                            {categorias.map((cat, idx) => (
                                <option key={cat.idCategoria || cat.IdCategoria || `cat-${idx}`} value={cat.nombre || cat.Nombre}>
                                    {cat.nombre || cat.Nombre}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </section>

            {/* CONTENIDO */}
            {loading ? (
                <p style={{ textAlign: 'center', color: '#94a3b8', padding: '3rem' }}>
                    Cargando foros...
                </p>
            ) : foros.length === 0 ? (
                <div style={{
                    textAlign: 'center',
                    padding: '4rem 2rem',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                }}>
                    <p style={{ fontSize: '1.125rem', color: '#64748b', marginBottom: '0.5rem' }}>
                        No hay foros activos
                    </p>
                    <p style={{ fontSize: '0.875rem', color: '#94a3b8' }}>
                        Cuando se creen foros, aparecerán aquí
                    </p>
                </div>
            ) : forosFiltrados.length === 0 ? (
                <div style={{
                    textAlign: 'center',
                    padding: '4rem 2rem',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                }}>
                    <p style={{ fontSize: '1.125rem', color: '#64748b', marginBottom: '0.5rem' }}>
                        No se encontraron foros
                    </p>
                    <p style={{ fontSize: '0.875rem', color: '#94a3b8' }}>
                        Intenta ajustar los filtros de búsqueda
                    </p>
                </div>
            ) : (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: '1rem',
                }}>
                    {forosFiltrados.map((foro, idx) => {
                        const comentarios = getComentariosForo(foro.id);
                        const numeroRespuestas = comentarios.length;

                        return (
                            <article
                                key={foro.id || `foro-${idx}`}
                                onClick={() => handleVerDetalle(foro)}
                                style={{
                                    backgroundColor: foro.destacado ? '#fffbeb' : '#f0f9ff',
                                    border: `1px solid ${foro.destacado ? '#fbbf24' : '#3b82f6'}`,
                                    borderRadius: '16px',
                                    overflow: 'hidden',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    cursor: 'pointer',
                                    position: 'relative',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    height: '100%',
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)';
                                    e.currentTarget.style.transform = 'translateY(-6px)';
                                    e.currentTarget.style.borderColor = foro.destacado ? '#fbbf24' : '#3b82f6';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.borderColor = foro.destacado ? '#fbbf24' : '#3b82f6';
                                }}
                            >
                                {/* Ribbon de importancia */}
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    width: '40px',
                                    height: '40px',
                                    zIndex: 10,
                                    overflow: 'hidden'
                                }}>
                                    <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        right: 0,
                                        width: 0,
                                        height: 0,
                                        borderStyle: 'solid',
                                        borderWidth: '0 40px 40px 0',
                                        borderColor: `transparent ${foro.destacado ? '#fbbf24' : '#3b82f6'} transparent transparent`,
                                    }} />
                                    <div style={{
                                        position: 'absolute',
                                        top: foro.destacado ? '2px' : '3px',
                                        right: '5px',
                                        color: 'white',
                                        fontSize: foro.destacado ? '1rem' : '0.75rem',
                                        fontWeight: 'bold',
                                        zIndex: 11
                                    }} title={foro.destacado ? "Foro Destacado" : "Foro Activo"}>
                                        {foro.destacado ? '★' : 'F'}
                                    </div>
                                </div>
                                
                                {/* Imagen */}
                                <div style={{
                                    width: '100%',
                                    height: '160px',
                                    overflow: 'hidden',
                                    backgroundColor: '#f8fafc',
                                    position: 'relative'
                                }}>
                                    <img
                                        src={getImageUrl(foro.imagen) || PLACEHOLDER_IMAGE}
                                        alt={foro.titulo}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                        }}
                                        onError={(e) => {
                                            if (e.target.src !== PLACEHOLDER_IMAGE) {
                                                e.target.src = PLACEHOLDER_IMAGE;
                                            }
                                        }}
                                    />
                                    {foro.destacado && (
                                        <div style={{
                                            position: 'absolute',
                                            bottom: '8px',
                                            left: '8px',
                                            backgroundColor: '#fffbeb',
                                            color: '#b45309',
                                            padding: '2px 8px',
                                            borderRadius: '6px',
                                            fontSize: '0.65rem',
                                            fontWeight: '700',
                                            border: '1px solid #fde68a'
                                        }}>
                                            Destacado
                                        </div>
                                    )}
                                </div>

                                {/* Contenido */}
                                <div style={{ padding: '1rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
                                    {/* Area & Category badges */}
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '0.75rem' }}>
                                        <span style={{
                                            backgroundColor: '#eff6ff',
                                            color: '#1e40af',
                                            padding: '1px 6px',
                                            borderRadius: '4px',
                                            fontSize: '0.6rem',
                                            fontWeight: '700',
                                            textTransform: 'uppercase',
                                            border: '1px solid #bfdbfe'
                                        }}>
                                            {foro.nombreArea || "Corporativo"}
                                        </span>
                                        {foro.categoria && (
                                            <span style={{
                                                backgroundColor: '#f1f5f9',
                                                color: '#475569',
                                                padding: '1px 6px',
                                                borderRadius: '4px',
                                                fontSize: '0.6rem',
                                                fontWeight: '700',
                                                textTransform: 'uppercase',
                                                border: '1px solid #e2e8f0'
                                            }}>
                                                {foro.categoria}
                                            </span>
                                        )}
                                        <span style={{
                                            backgroundColor: '#f0fdf4',
                                            color: '#166534',
                                            padding: '1px 6px',
                                            borderRadius: '4px',
                                            fontSize: '0.6rem',
                                            fontWeight: '700',
                                            border: '1px solid #bbf7d0'
                                        }}>
                                            💬 {numeroRespuestas}
                                        </span>
                                    </div>

                                    {/* Título */}
                                    <h3 style={{
                                        fontSize: '0.95rem',
                                        fontWeight: '700',
                                        color: '#1e293b',
                                        margin: '0 0 0.5rem 0',
                                        lineHeight: '1.4',
                                        display: '-webkit-box',
                                        WebkitLineClamp: '2',
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        minHeight: '2.8em'
                                    }}>
                                        {foro.titulo}
                                    </h3>

                                    {/* Descripción */}
                                    <p style={{
                                        fontSize: '0.8rem',
                                        color: '#64748b',
                                        marginBottom: '1rem',
                                        lineHeight: '1.5',
                                        display: '-webkit-box',
                                        WebkitLineClamp: '3',
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        flex: 1
                                    }}>
                                        {foro.descripcion}
                                    </p>

                                    {/* Footer */}
                                    <div style={{
                                        paddingTop: '0.75rem',
                                        marginTop: 'auto',
                                        borderTop: '1px solid #f1f5f9',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        fontSize: '0.7rem',
                                        color: '#94a3b8'
                                    }}>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '500' }}>
                                            👤 {usuariosMap[foro.creadoPor]?.split(' ')[0] || "Sistema"}
                                        </span>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            📅 {new Date(foro.fecha).toLocaleDateString()}
                                        </span>
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}

            {/* MODAL TIPO YOUTUBE */}
            {showModal && foroSeleccionado && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                        padding: '2rem'
                    }}
                    onClick={() => setShowModal(false)}
                >
                    <div
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            width: '95%',
                            maxWidth: '1000px',
                            height: '85vh',
                            maxHeight: '90vh',
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                            overflow: 'hidden'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div style={{
                            padding: '1.25rem 2rem',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            backgroundColor: 'white',
                            borderBottom: '1px solid #f1f5f9'
                        }}>
                            <h3 style={{
                                margin: 0,
                                fontSize: '1.25rem',
                                color: 'var(--color-primary)',
                                fontWeight: 800,
                                letterSpacing: '-0.02em'
                            }}>
                                Foro Temático
                            </h3>
                            <button
                                onClick={() => setShowModal(false)}
                                style={{
                                    background: '#f1f5f9',
                                    border: 'none',
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '1.2rem',
                                    cursor: 'pointer',
                                    color: '#64748b',
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e2e8f0'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
                            >
                                ×
                            </button>
                        </div>

                        {/* Modal Navigation - Tabs */}
                        <div style={{
                            display: 'flex',
                            borderBottom: '1px solid #e2e8f0',
                            backgroundColor: 'white',
                        }}>
                            <button
                                onClick={() => setModalTab('info')}
                                style={{
                                    flex: 1,
                                    padding: '1.25rem 1rem',
                                    border: 'none',
                                    borderBottom: `3px solid ${modalTab === 'info' ? 'var(--color-primary)' : 'transparent'}`,
                                    background: 'none',
                                    fontWeight: 700,
                                    fontSize: '0.9rem',
                                    color: modalTab === 'info' ? 'var(--color-primary)' : '#64748b',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    outline: 'none',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em'
                                }}
                            >
                                Información
                            </button>
                            <button
                                onClick={() => setModalTab('comments')}
                                style={{
                                    flex: 1,
                                    padding: '1.25rem 1rem',
                                    border: 'none',
                                    borderBottom: `3px solid ${modalTab === 'comments' ? 'var(--color-primary)' : 'transparent'}`,
                                    background: 'none',
                                    fontWeight: 700,
                                    fontSize: '0.9rem',
                                    color: modalTab === 'comments' ? 'var(--color-primary)' : '#64748b',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    outline: 'none',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em'
                                }}
                            >
                                Comentarios ({getComentariosForo(foroSeleccionado.id).length})
                            </button>
                        </div>

                        {/* Modal Content Area */}
                        <div style={{
                            display: 'flex',
                            flex: 1,
                            overflow: 'hidden',
                            backgroundColor: modalTab === 'comments' ? '#f8fafc' : 'white'
                        }}>
                            {modalTab === 'info' ? (
                                <div style={{
                                    flex: 1,
                                    overflowY: 'auto',
                                    padding: '2.5rem',
                                    display: 'flex',
                                    justifyContent: 'center'
                                }}>
                                    <div style={{ maxWidth: '900px', width: '100%' }}>
                                        {/* Imagen */}
                                        {foroSeleccionado.imagen && (
                                            <div style={{
                                                marginBottom: '2rem',
                                                borderRadius: '16px',
                                                overflow: 'hidden',
                                                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                                                maxHeight: '450px',
                                                backgroundColor: '#f1f5f9'
                                            }}>
                                                <img
                                                    src={getImageUrl(foroSeleccionado.imagen) || PLACEHOLDER_IMAGE}
                                                    alt={foroSeleccionado.titulo}
                                                    style={{
                                                        width: '100%',
                                                        height: 'auto',
                                                        maxHeight: '450px',
                                                        objectFit: 'cover',
                                                        display: 'block'
                                                    }}
                                                    onError={(e) => {
                                                        if (e.target.src !== PLACEHOLDER_IMAGE) {
                                                            e.target.src = PLACEHOLDER_IMAGE;
                                                        }
                                                    }}
                                                />
                                            </div>
                                        )}

                                        {/* Meta info */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                                            <span
                                                className="intra-chip"
                                                style={{
                                                    padding: '0.5rem 1rem',
                                                    borderRadius: '12px',
                                                    fontSize: '0.875rem',
                                                    fontWeight: 600,
                                                    backgroundColor: '#e0e7ff',
                                                    color: '#3730a3',
                                                }}
                                            >
                                                {foroSeleccionado.categoria}
                                            </span>
                                            {foroSeleccionado.fecha && (
                                                <span style={{ fontSize: '0.875rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <strong>📅 Publicado:</strong> {formatFechaCorta(foroSeleccionado.fecha)}
                                                </span>
                                            )}
                                        </div>

                                        {/* Título */}
                                        <h3 style={{
                                            fontSize: '2.25rem',
                                            fontWeight: 800,
                                            color: '#1e293b',
                                            margin: '0 0 1.5rem 0',
                                            lineHeight: 1.2
                                        }}>
                                            {foroSeleccionado.titulo}
                                        </h3>

                                        {/* Descripción completa */}
                                        <div style={{
                                            fontSize: '1.125rem',
                                            color: '#334155',
                                            lineHeight: 1.8,
                                            whiteSpace: 'pre-wrap',
                                            padding: '1.5rem',
                                            backgroundColor: '#f8fafc',
                                            borderRadius: '12px',
                                            borderLeft: '4px solid #cbd5e1'
                                        }}>
                                            {foroSeleccionado.descripcion || "Sin descripción disponible"}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div style={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    overflow: 'hidden',
                                    maxWidth: '1000px',
                                    margin: '0 auto',
                                    width: '100%',
                                    position: 'relative'
                                }}>
                                    {/* Comments Title Slim */}
                                    <div style={{
                                        padding: '1rem 2rem',
                                        background: 'white',
                                        borderBottom: '1px solid #e2e8f0',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}>
                                        <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#1e293b' }}>
                                            Conversación
                                        </h4>
                                        <span style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: 600 }}>
                                            {getComentariosForo(foroSeleccionado.id).length} mensajes
                                        </span>
                                    </div>

                                    {/* Comments List Area */}
                                    <div style={{ flex: 1, overflowY: 'auto' }}>
                                        <CommentList
                                            comentarios={getComentariosForo(foroSeleccionado.id)}
                                            formatFecha={formatFechaCorta}
                                            foroSeleccionado={foroSeleccionado}
                                            fotosMap={fotosMap}
                                        />
                                    </div>

                                    {/* Comment Input Area - Fixed at bottom of tab */}
                                    <div style={{
                                        padding: '1.25rem 2rem',
                                        borderTop: '1px solid #e2e8f0',
                                        background: 'white',
                                        boxShadow: '0 -4px 10px rgba(0,0,0,0.03)'
                                    }}>
                                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
                                            {/* User Avatar */}
                                            <div style={{
                                                width: '36px',
                                                height: '36px',
                                                minWidth: '36px',
                                                borderRadius: '50%',
                                                backgroundColor: fotoUsuario ? 'transparent' : 'var(--color-primary)',
                                                color: 'white',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '0.85rem',
                                                fontWeight: 'bold',
                                                marginBottom: '6px',
                                                overflow: 'hidden',
                                                border: '2px solid #f1f5f9'
                                            }}>
                                                {fotoUsuario ? (
                                                    <img
                                                        src={getImageUrl(fotoUsuario)}
                                                        alt={nombreUsuario}
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling.style.display = 'flex'; }}
                                                    />
                                                ) : null}
                                                <div style={{ display: fotoUsuario ? 'none' : 'flex', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                                                    {nombreUsuario ? nombreUsuario.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : "YO"}
                                                </div>
                                            </div>

                                            {/* Input Area */}
                                            <div style={{
                                                flex: 1,
                                                backgroundColor: '#f1f5f9',
                                                borderRadius: '24px',
                                                padding: '0.75rem 1.25rem',
                                                display: 'flex',
                                                alignItems: 'center',
                                                border: '1px solid #e2e8f0'
                                            }}>
                                                <textarea
                                                    value={nuevoComentario}
                                                    onChange={(e) => setNuevoComentario(e.target.value)}
                                                    placeholder="Escribe un comentario..."
                                                    rows={1}
                                                    style={{
                                                        width: '100%',
                                                        border: 'none',
                                                        background: 'transparent',
                                                        outline: 'none',
                                                        resize: 'none',
                                                        fontSize: '1rem',
                                                        padding: 0,
                                                        maxHeight: '120px',
                                                        fontFamily: 'inherit',
                                                        color: '#334155'
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' && !e.shiftKey) {
                                                            e.preventDefault();
                                                            if (nuevoComentario.trim() && nombreUsuario) {
                                                                handleEnviarComentario(e);
                                                            }
                                                        }
                                                    }}
                                                />
                                            </div>

                                            {/* Send Button */}
                                            <button
                                                type="button"
                                                onClick={handleEnviarComentario}
                                                disabled={!nuevoComentario.trim() || !nombreUsuario || isSubmitting}
                                                style={{
                                                    backgroundColor: (nuevoComentario.trim() && nombreUsuario && !isSubmitting) ? 'var(--color-primary)' : '#e2e8f0',
                                                    border: 'none',
                                                    borderRadius: '50%',
                                                    width: '44px',
                                                    height: '44px',
                                                    minWidth: '44px',
                                                    cursor: (nuevoComentario.trim() && nombreUsuario && !isSubmitting) ? 'pointer' : 'default',
                                                    color: 'white',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    transition: 'all 0.2s ease',
                                                    opacity: isSubmitting ? 0.7 : 1,
                                                    boxShadow: (nuevoComentario.trim() && nombreUsuario && !isSubmitting) ? '0 4px 6px rgba(0,0,0,0.1)' : 'none'
                                                }}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <line x1="22" y1="2" x2="11" y2="13"></line>
                                                    <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ForosUsuario;
